import { useSyncExternalStore } from 'react';

/**
 * learnerState — a tiny, app-wide store for the learner's CURRENT session context: the
 * things chosen on one screen (Home) that another screen (the Learning Session + its loader)
 * needs to read, that aren't part of any one component's props.
 *
 * Today it holds the current concept (set when a concept is started from Home, cleared when
 * the session ends). It is intentionally GENERIC so it can grow — e.g. `currentAura`,
 * `currentGrade` — without a new module each time. Add a field to the state object and a
 * matching setter; keep each setter guarded.
 *
 * Pattern: a module-level snapshot + a Set of listeners, exposed to React via
 * `useSyncExternalStore`. Mirrors `lib/companion.js` as a single source of truth, but mutable.
 * Not persisted (in-memory only): a reload starts fresh, which is correct — a session that
 * isn't in progress has no "current concept".
 *
 * It also holds `sessionHint` — a one-shot line of warm copy handed from one screen to the
 * next across a navigation. The /learn route guard sets it when it bounces a student Home
 * (no concept/objective in the store — e.g. after a page reload), and Home reads it, shows it
 * and clears it. In-memory is exactly right here: a manual reload has no hint to explain.
 *
 * It also holds `missionPreview` — the land and concept names of the mission a student just
 * tapped on Home. The /mission route carries only opaque ids, so without this the Recall
 * loader would open with a blank title and wait for the questions GET to tell it a name Home
 * already had on screen. Same one-shot spirit as `sessionHint`: written on the way out of
 * Home, read on the next screen's first frame, and superseded the moment the real response
 * lands. A deep link or reload simply has none, and the loader waits as before.
 *
 * @typedef {{ conceptId: string, name: string, subject?: string, loId?: string, loName?: string }} CurrentConcept
 * @typedef {{ conceptId: string, setId: number, land: string, concept: string }} MissionPreview
 * It also holds `sessionNonce` — a counter bumped by every `setCurrentConcept`. /learn keys its
 * session on it, so starting the NEXT objective from inside /learn (the "Up next" card at the end
 * of a session) remounts the page with fresh state instead of wouter reusing the old element for
 * the same path. It counts starts, not concepts: the same objective started twice is two
 * sessions.
 *
 * @typedef {{ currentConcept: CurrentConcept | null, sessionHint: string | null, missionPreview: MissionPreview | null, sessionNonce: number }} LearnerState
 */

/** @type {LearnerState} */
let state = { currentConcept: null, sessionHint: null, missionPreview: null, sessionNonce: 0 };
const listeners = new Set();

function emit() {
  for (const l of listeners) l();
}

/** Current snapshot (stable reference until a setter replaces it). */
export function getLearnerState() {
  return state;
}

/**
 * Set the concept the student just started from Home. No-op (and a warning) when neither a
 * usable id nor name is present — the store should never hold a half-empty concept.
 *
 * `loId`/`loName` are stored when present but are NEVER a precondition of this setter — a
 * recall mission has no learning objective and must keep working, so the no-id-and-no-name
 * guard below deliberately does not consider them.
 * @param {CurrentConcept} concept
 */
export function setCurrentConcept(concept) {
  const conceptId = concept && typeof concept.conceptId === 'string' ? concept.conceptId.trim() : '';
  const name = concept && typeof concept.name === 'string' ? concept.name.trim() : '';
  if (!conceptId && !name) {
    console.warn('learnerState.setCurrentConcept: ignoring concept with no id or name', concept);
    return;
  }
  const subject = concept && typeof concept.subject === 'string' ? concept.subject : undefined;
  const loId = concept && typeof concept.loId === 'string' ? concept.loId.trim() || undefined : undefined;
  const loName = concept && typeof concept.loName === 'string' ? concept.loName.trim() || undefined : undefined;
  state = { ...state, currentConcept: { conceptId, name, subject, loId, loName }, sessionNonce: state.sessionNonce + 1 };
  emit();
}

/**
 * Set the one-shot hint the next screen should show (see the sessionHint note above). A
 * non-string or blank value clears it rather than parking an empty toast on Home.
 * @param {string} hint
 */
export function setSessionHint(hint) {
  const next = typeof hint === 'string' ? hint.trim() || null : null;
  if (state.sessionHint === next) return;
  state = { ...state, sessionHint: next };
  emit();
}

/** Clear the hint (call once it has been shown or dismissed). */
export function clearSessionHint() {
  if (state.sessionHint === null) return;
  state = { ...state, sessionHint: null };
  emit();
}

/**
 * Remember the land + concept of the mission just started from Home, so the Recall loader can
 * title itself on frame 1 instead of waiting for the questions GET.
 *
 * Keyed by `conceptId`/`setId` so a stale preview can never title the WRONG mission: the
 * reader compares both before trusting it. A preview with neither name is not worth keeping —
 * the loader's own fallback is identical — so it is ignored with a warning.
 *
 * @param {MissionPreview} preview
 */
export function setMissionPreview(preview) {
  const conceptId = preview && typeof preview.conceptId === 'string' ? preview.conceptId.trim() : '';
  const setId = Number(preview?.setId);
  const land = preview && typeof preview.land === 'string' ? preview.land.trim() : '';
  const concept = preview && typeof preview.concept === 'string' ? preview.concept.trim() : '';
  if (!conceptId || !Number.isFinite(setId) || (!land && !concept)) {
    console.warn('learnerState.setMissionPreview: ignoring incomplete preview', preview);
    return;
  }
  state = { ...state, missionPreview: { conceptId, setId, land, concept } };
  emit();
}

/**
 * The preview for this exact mission, or null. Returns null for a DIFFERENT mission rather
 * than the stale one — titling a mission with another's land would be worse than a blank.
 * @param {string} conceptId
 * @param {number} setId
 * @returns {MissionPreview | null}
 */
export function getMissionPreview(conceptId, setId) {
  const p = state.missionPreview;
  if (!p) return null;
  return p.conceptId === conceptId && p.setId === Number(setId) ? p : null;
}

/** Clear the mission preview (call once the real questions response has landed). */
export function clearMissionPreview() {
  if (state.missionPreview === null) return;
  state = { ...state, missionPreview: null };
  emit();
}

/** Clear the current concept (call when a session ends). */
export function clearCurrentConcept() {
  if (state.currentConcept === null) return;
  state = { ...state, currentConcept: null };
  emit();
}

/** Subscribe to learner-state changes via React. */
export function useLearnerState() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getLearnerState,
    getLearnerState,
  );
}
